import { Flex, FormLabel, Input } from "@chakra-ui/react";
import React, { useCallback, useMemo, useRef } from "react";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { Field, FieldProps, Form, Formik, FormikProps } from "formik";
import AppDatePicker from "@/features/line-sheet-sets/app-date-picker";
import AppFileController from "@/features/inventories/app-file-controller";
import { useInventoryDumbTransaction } from "@/features/inventories/hooks/use-inventory-dumb-transaction";
import useIdentity from "@/features/ui/hooks/use-identity";
import * as Yup from "yup";
import useYupHelper from "@/features/ui/form/use-yup-helper";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import AppCompanySelect from "@/features/line-sheet-sets/app-company-select";
import AppFormModal from "@/features/ui/app-form-modal";

interface UploadInventoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormFields {
  name: string;
  comment?: string;
  organizedOn: Date;
  organizedBy?: { id: number };
  files: File[];
}

export default function UploadInventoryModal({
  isOpen,
  onClose,
}: UploadInventoryModalProps) {
  const identity = useIdentity();
  const { t, tTitle } = useI18nHelper();
  const formikRef = useRef<FormikProps<FormFields>>(null);
  const { addCreateInventory } = useInventoryDumbTransaction();
  const { withMessage } = useYupHelper({
    prefix: "upload_inventory_form",
    ns: "inventories",
  });
  const { error: showError } = useAppToasts({
    id: "UPLOAD_INVENTORY_FORM",
  });

  const validateSchema = useMemo(() => {
    return Yup.object({
      name: Yup.string().label("title").required(withMessage.required),
      files: Yup.array().min(1, withMessage.min),
      organizedBy: Yup.object().required(withMessage.required),
    });
  }, [withMessage]);

  const validate = useCallback(
    async (values: FormFields) => {
      try {
        await validateSchema.validate(values, { abortEarly: false });
      } catch (e: any) {
        showError(e);
        return e;
      }
    },
    [validateSchema, showError]
  );

  const handleSubmit = useCallback(
    async (values: FormFields) => {
      if (identity?.company?.type) {
        addCreateInventory(
          { by: identity.company.type, companyId: identity.company.id },
          {
            name: values.name,
            comment: values.comment || null,
            organizedOn: values.organizedOn,
            organizedBy: values.organizedBy!,
            files: values.files,
          }
        );
        onClose();
      }
    },
    [identity, addCreateInventory, onClose]
  );

  return (
    <Formik<FormFields>
      initialValues={{
        name: "",
        comment: "",
        organizedOn: (() => {
          const localDate = new Date();
          localDate.setHours(0, 0, 0, 0);
          return localDate;
        })(),
        organizedBy: (() => {
          if (identity?.company?.type === "BUYER") {
            return {
              id: identity.company.id,
            };
          }
          return undefined;
        })(),
        files: [],
      }}
      validateOnChange={false}
      validateOnBlur={false}
      validateOnMount={false}
      validate={validate}
      onSubmit={(values, formikHelpers) => {
        return handleSubmit(values);
      }}
      innerRef={formikRef}
    >
      <AppFormModal
        isOpen={isOpen}
        onClose={onClose}
        title={"upload_inventory"}
        submitName={"upload"}
      >
        <Form>
          <Flex flexDirection={"column"} fontSize={"12px"} gap={"8px"}>
            <Field name={"name"}>
              {(props: FieldProps<string, FormFields>) => {
                return (
                  <Flex flexDirection={"column"}>
                    <FormLabel fontSize={"inherit"} color={"#6F6F6F"}>
                      {tTitle("title")}
                    </FormLabel>
                    <Input
                      borderRadius={"4px"}
                      fontSize={"inherit"}
                      height={"auto"}
                      p={"4px 12px"}
                      autoComplete={"off"}
                      placeholder={t("title_placeholder")}
                      {...props.field}
                    />
                  </Flex>
                );
              }}
            </Field>

            <Field name={"organizedOn"}>
              {(props: FieldProps<Date, FormFields>) => {
                return (
                  <Flex flexDirection={"column"}>
                    <FormLabel fontSize={"inherit"} color={"#6F6F6F"}>
                      {tTitle("organized_on")}
                    </FormLabel>
                    <AppDatePicker
                      name={tTitle("organized_on")}
                      value={props.field.value}
                      showTimeInput={false}
                      onChange={(value) => {
                        props.form.setFieldValue(props.field.name, value);
                      }}
                    />
                  </Flex>
                );
              }}
            </Field>

            {identity?.company?.type === "AGENCY" && (
              <Field name={"organizedBy"}>
                {(props: FieldProps<{ id: number }, FormFields>) => {
                  return (
                    <Flex flexDirection={"column"}>
                      <FormLabel fontSize={"inherit"} color={"#6F6F6F"}>
                        {tTitle("organized_by")}
                      </FormLabel>
                      <AppCompanySelect
                        width={"100%"}
                        name={tTitle("organized_by")}
                        types={["BUYER"]}
                        id={props.field.value?.id}
                        onSelect={(company) => {
                          const value = company
                            ? { id: company.id }
                            : undefined;
                          props.form.setFieldValue(props.field.name, value);
                        }}
                      />
                    </Flex>
                  );
                }}
              </Field>
            )}

            <Field name={"comment"}>
              {(props: FieldProps<string, FormFields>) => {
                return (
                  <Flex flexDirection={"column"}>
                    <FormLabel fontSize={"inherit"} color={"#6F6F6F"}>
                      {`${tTitle("comment")} (${tTitle("optional")})`}
                    </FormLabel>
                    <Input
                      borderRadius={"4px"}
                      fontSize={"inherit"}
                      height={"auto"}
                      p={"4px 12px"}
                      autoComplete={"off"}
                      placeholder={t("common:comment_placeholder")}
                      {...props.field}
                    />
                  </Flex>
                );
              }}
            </Field>

            <Field name={"files"}>
              {(props: FieldProps<File[], FormFields>) => {
                return (
                  <AppFileController
                    isMultiple={true}
                    value={props.field.value}
                    onChange={(value) => {
                      props.form.setFieldValue(props.field.name, value);
                    }}
                  />
                );
              }}
            </Field>
          </Flex>
        </Form>
      </AppFormModal>
    </Formik>
  );
}
